import React, { useState, useEffect } from 'react';
import { MdArrowBack, MdCheck, MdClose, MdAccessTime, MdRateReview } from 'react-icons/md';
import {
  getMySubmission,
  getAppliedRubricItems,
  getRubricsByAssignment,
  getFeedback,
  createRegradeRequest,
  getRegradeRequests
} from '../../utils/api/AssignmentsApi';

const StudentGradeView = ({ assignment, classContext, onBack }) => {
  const [submission, setSubmission] = useState(null);
  const [rubrics, setRubrics] = useState([]);
  const [appliedItems, setAppliedItems] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Regrade request state
  const [showRegradeModal, setShowRegradeModal] = useState(false);
  const [regradeReason, setRegradeReason] = useState('');
  const [isSubmittingRegrade, setIsSubmittingRegrade] = useState(false);
  const [existingRegrade, setExistingRegrade] = useState(null);

  useEffect(() => {
    loadGradeData();
  }, [assignment.assignment_id]);

  const loadGradeData = async () => {
    try {
      setLoading(true);
      setError(null);

      const sub = await getMySubmission(assignment.assignment_id, classContext);
      if (!sub) {
        setError('No submission found for this assignment.');
        setLoading(false);
        return;
      }

      setSubmission(sub);

      const [rubricsResult, appliedResult, feedbackResult] = await Promise.all([
        getRubricsByAssignment(assignment.assignment_id, classContext),
        getAppliedRubricItems(sub.submission_id, classContext),
        getFeedback(sub.submission_id, null, classContext)
      ]);

      setRubrics(rubricsResult.rubrics || []);
      setAppliedItems(appliedResult.applied_rubric_items || []);
      setFeedback(feedbackResult.feedback || []);

      // Check for existing regrade request
      try {
        const regradeResult = await getRegradeRequests({
          assignment_id: assignment.assignment_id,
          student_id: classContext.username
        }, classContext);
        const requests = regradeResult.regrade_requests || [];
        if (requests.length > 0) setExistingRegrade(requests[0]);
      } catch (regradeErr) {
        // Non-critical, ignore
      }
    } catch (err) {
      console.error('Error loading grade data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const overallFeedback = feedback.filter(f => f.feedback_type === 'overall');

  // Calculate score from applied items
  const totalAwarded = appliedItems.reduce((sum, item) => sum + (item.points_awarded || 0), 0);
  const totalMax = assignment.total_points || 0;
  const percentage = totalMax > 0 ? Math.round((totalAwarded / totalMax) * 100) : 0;

  const handleSubmitRegrade = async () => {
    if (!regradeReason.trim()) return;
    setIsSubmittingRegrade(true);
    try {
      const result = await createRegradeRequest({
        assignment_id: assignment.assignment_id,
        submission_id: submission.submission_id,
        reason: regradeReason.trim()
      }, classContext);
      setExistingRegrade(result.regrade_request || { status: 'pending', reason: regradeReason.trim() });
      setShowRegradeModal(false);
      setRegradeReason('');
    } catch (err) {
      console.error('Error submitting regrade request:', err);
      alert(err.message || 'Failed to submit regrade request');
    } finally {
      setIsSubmittingRegrade(false);
    }
  };

  const getScoreColor = (pct) => {
    if (pct >= 90) return 'text-green-600';
    if (pct >= 80) return 'text-blue-600';
    if (pct >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (pct) => {
    if (pct >= 90) return 'bg-green-50 border-green-200';
    if (pct >= 80) return 'bg-blue-50 border-blue-200';
    if (pct >= 70) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading grade...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600">
            <MdArrowBack className="text-lg" />
          </button>
          <span className="font-semibold text-gray-900">{assignment.title}</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 text-sm mb-3">{error}</p>
            <button onClick={onBack} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              Back to Assignments
            </button>
          </div>
        </div>
      </div>
    );
  }

  const components = assignment.components || [];

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4 flex-shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600">
          <MdArrowBack className="text-lg" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-gray-900 m-0">{assignment.title}</h2>
          <div className="flex items-center gap-3 mt-1">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
              submission.status === 'returned' ? 'bg-green-100 text-green-700' :
              submission.status === 'graded' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {submission.status === 'returned' ? 'Graded' : submission.status}
            </span>
            {submission.submitted_at && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <MdAccessTime className="text-xs" />
                Submitted {new Date(submission.submitted_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Score Card */}
          <div className={`rounded-xl border p-6 ${getScoreBg(percentage)}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Your Score</p>
                <div className={`text-4xl font-bold ${getScoreColor(percentage)}`}>
                  {totalAwarded}/{totalMax}
                </div>
                <p className={`text-lg font-semibold mt-1 ${getScoreColor(percentage)}`}>
                  {percentage}%
                </p>
              </div>
              {/* Score ring */}
              <div className="relative w-24 h-24">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9" fill="none"
                    stroke={percentage >= 90 ? '#22c55e' : percentage >= 80 ? '#3b82f6' : percentage >= 70 ? '#eab308' : '#ef4444'}
                    strokeWidth="3"
                    strokeDasharray={`${percentage} ${100 - percentage}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-lg font-bold ${getScoreColor(percentage)}`}>{percentage}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Feedback */}
          {overallFeedback.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Instructor Feedback</h3>
              {overallFeedback.map((fb, idx) => (
                <div key={idx} className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {fb.content}
                </div>
              ))}
            </div>
          )}

          {/* Rubric Breakdown */}
          {rubrics.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 m-0">Score Breakdown</h3>
              </div>

              {rubrics.map(rubric => {
                const items = (rubric.items || []).filter(i => i.is_active !== false);
                const component = components.find(c => c.component_id === rubric.component_id);

                return (
                  <div key={rubric.rubric_id}>
                    {components.length > 1 && component && (
                      <div className="px-5 py-2 bg-gray-50 border-b border-gray-100">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          {component.title} ({component.points} pts)
                        </span>
                      </div>
                    )}
                    <div className="divide-y divide-gray-50">
                      {items.map(item => {
                        const applied = appliedItems.find(a => a.rubric_item_id === item.item_id);
                        const isApplied = !!applied;
                        const awarded = applied?.points_awarded || 0;

                        return (
                          <div key={item.item_id} className="px-5 py-3 flex items-start gap-3">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isApplied ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              {isApplied ? (
                                <MdCheck className="text-green-600 text-xs" />
                              ) : (
                                <MdClose className="text-gray-400 text-xs" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium text-gray-800">{item.title}</span>
                                <span className={`text-sm font-semibold flex-shrink-0 ${
                                  isApplied ? 'text-green-600' : 'text-gray-400'
                                }`}>
                                  {isApplied ? awarded : 0}/{item.max_points}
                                </span>
                              </div>
                              {item.description && (
                                <p className="text-xs text-gray-500 mt-0.5 m-0">{item.description}</p>
                              )}
                              {applied?.feedback && (
                                <div className="mt-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                                  <p className="text-xs text-blue-700 m-0">{applied.feedback}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Regrade Request Section */}
          {submission && (submission.status === 'returned' || submission.status === 'graded') && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Regrade Request</h3>
              {existingRegrade ? (
                <div className={`p-4 rounded-lg border ${
                  existingRegrade.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                  existingRegrade.status === 'approved' ? 'bg-green-50 border-green-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      existingRegrade.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      existingRegrade.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {existingRegrade.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-1"><strong>Your reason:</strong> {existingRegrade.reason}</p>
                  {existingRegrade.resolution_notes && (
                    <p className="text-sm text-gray-700"><strong>Instructor response:</strong> {existingRegrade.resolution_notes}</p>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    If you believe your grade was incorrect, you can request a regrade review.
                  </p>
                  <button
                    onClick={() => setShowRegradeModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <MdRateReview className="text-base" />
                    Request Regrade
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Regrade Request Modal */}
      {showRegradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 m-0">Request Regrade</h3>
              <p className="text-sm text-gray-500 mt-1 m-0">Explain why you believe your grade should be reviewed.</p>
            </div>
            <div className="p-5">
              <textarea
                value={regradeReason}
                onChange={(e) => setRegradeReason(e.target.value)}
                placeholder="Describe which rubric items you want reviewed and why..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={5}
              />
            </div>
            <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowRegradeModal(false); setRegradeReason(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRegrade}
                disabled={!regradeReason.trim() || isSubmittingRegrade}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {isSubmittingRegrade ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentGradeView;
